import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * BLOCK 98 Part A ,  a real React error boundary. Any render/runtime error in
 * a wrapped subtree degrades to a visible, self-contained error card with a
 * retry, instead of throwing up the tree and unmounting the entire app to a
 * blank screen. This is defense-in-depth: it does NOT replace fixing the
 * underlying bug, it just contains the blast radius of any future one.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.section ? ` · ${this.props.section}` : ""}]`, error, info?.componentStack);
    // BLOCK 184 Part B.2 ,  best-effort: a crash survives beyond this
    // browser's console. Never let a logging failure compound the crash.
    api.post("/client-errors", {
      section: this.props.section || null,
      message: String(error?.message || error),
      stack: info?.componentStack || error?.stack || null,
      url: window.location.href,
    }).catch(() => {});
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary-fallback"
          className="ae-card p-8 border border-rose-500/30 bg-rose-500/[0.05]"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-rose-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-xl tracking-tight text-rose-100">
                {this.props.section ? `${this.props.section} hit an error` : "Something went wrong"}
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                This section failed to render, but the rest of the app is still
                running. You can retry, or reload the page.
              </p>
              {this.state.error?.message && (
                <pre className="mt-3 text-xs text-rose-300/80 bg-black/40 rounded-sm p-3 overflow-auto max-h-32 whitespace-pre-wrap">
                  {String(this.state.error.message)}
                </pre>
              )}
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={this.reset}
                  data-testid="error-boundary-retry"
                  className="btn-primary text-sm inline-flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" /> Retry
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-ghost text-sm"
                >
                  Reload page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
